import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { LanguageProvider } from '@/context/LanguageContext';
import { Toaster } from 'sonner';
import '@/App.css';

// Public
import Home from '@/pages/Home';
import Login from '@/pages/Login';
import Register from '@/pages/Register';
import KnowledgeHub from '@/pages/KnowledgeHub';
import KnowledgeArticle from '@/pages/KnowledgeArticle';

// Client
import Dashboard from '@/pages/Dashboard';
import WebTrader from '@/pages/client/WebTrader';
import Transactions from '@/pages/client/Transactions';
import KYC from '@/pages/client/KYC';
import Support from '@/pages/client/Support';
import LegalDocuments from '@/pages/client/LegalDocuments';

// Admin
import AdminLayout from '@/pages/admin/AdminLayout';
import AdminOverview from '@/pages/admin/AdminOverview';
import AdminClients from '@/pages/admin/AdminClients';
import AdminClientDetail from '@/pages/admin/AdminClientDetail';
import AdminAccounts from '@/pages/admin/AdminAccounts';
import AdminOrders from '@/pages/admin/AdminOrders';
import AdminPositions from '@/pages/admin/AdminPositions';
import AdminInstruments from '@/pages/admin/AdminInstruments';
import AdminKYC from '@/pages/admin/AdminKYC';
import AdminTransactions from '@/pages/admin/AdminTransactions';
import AdminTickets from '@/pages/admin/AdminTickets';
import AdminAudit from '@/pages/admin/AdminAudit';
import AdminKnowledgeHub from '@/pages/admin/AdminKnowledgeHub';
import AdminWebTraderSettings from '@/pages/admin/AdminWebTraderSettings';
import AdminWebTraderPreview from '@/pages/admin/AdminWebTraderPreview';
import AdminLegalDocuments from '@/pages/admin/AdminLegalDocuments';
import AdminFooterSettings from '@/pages/admin/AdminFooterSettings';
import AdminPaymentSettings from '@/pages/admin/AdminPaymentSettings';
import AdminLoginHistory from '@/pages/admin/AdminLoginHistory';

const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="loader"></div></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
    return <Navigate to={user.role === 'CLIENT' ? '/dashboard' : '/admin'} replace />;
  }
  return children;
};

const Positions = () => (
  <div className="p-8">
    <h1 className="text-3xl font-bold text-brand-primary mb-4">Positions</h1>
    <p className="text-slate-600">View positions in the WebTrader.</p>
  </div>
);

const Profile = () => {
  const { user } = useAuth();
  return (
    <div className="p-8" data-testid="client-profile-page">
      <h1 className="text-3xl font-bold text-brand-primary mb-6">Profile</h1>
      <div className="bg-white p-6 rounded-xl border border-slate-200 max-w-2xl">
        <div className="space-y-4">
          <div><label className="text-sm text-slate-600">Full Name</label><div className="font-semibold">{user?.full_name}</div></div>
          <div><label className="text-sm text-slate-600">Email</label><div className="font-semibold">{user?.email}</div></div>
          <div><label className="text-sm text-slate-600">Country</label><div className="font-semibold">{user?.country || 'Not set'}</div></div>
          <div><label className="text-sm text-slate-600">KYC Status</label><div className="font-semibold">{user?.kyc_status}</div></div>
        </div>
      </div>
    </div>
  );
};

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/knowledge" element={<KnowledgeHub />} />
            <Route path="/knowledge/:slug" element={<KnowledgeArticle />} />

            <Route path="/dashboard" element={<ProtectedRoute allowedRoles={['CLIENT']}><Dashboard /></ProtectedRoute>}>
              <Route path="webtrader" element={<WebTrader />} />
              <Route path="positions" element={<Positions />} />
              <Route path="transactions" element={<Transactions />} />
              <Route path="kyc" element={<KYC />} />
              <Route path="support" element={<Support />} />
              <Route path="legal-documents" element={<LegalDocuments />} />
              <Route path="profile" element={<Profile />} />
            </Route>

            <Route path="/client/webtrader" element={<Navigate to="/dashboard/webtrader" replace />} />

            <Route path="/admin/webtrader" element={<ProtectedRoute allowedRoles={['ADMIN', 'SUPER_ADMIN']}><WebTrader /></ProtectedRoute>} />

            <Route path="/admin" element={<ProtectedRoute allowedRoles={['ADMIN', 'SUPER_ADMIN']}><AdminLayout /></ProtectedRoute>}>
              <Route index element={<AdminOverview />} />
              <Route path="clients" element={<AdminClients />} />
              <Route path="clients/:id" element={<AdminClientDetail />} />
              <Route path="accounts" element={<AdminAccounts />} />
              <Route path="orders" element={<AdminOrders />} />
              <Route path="positions" element={<AdminPositions />} />
              <Route path="instruments" element={<AdminInstruments />} />
              <Route path="kyc" element={<AdminKYC />} />
              <Route path="transactions" element={<AdminTransactions />} />
              <Route path="tickets" element={<AdminTickets />} />
              <Route path="audit" element={<AdminAudit />} />
              <Route path="knowledge" element={<AdminKnowledgeHub />} />
              <Route path="webtrader-settings" element={<AdminWebTraderSettings />} />
              <Route path="webtrader-preview" element={<AdminWebTraderPreview />} />
              <Route path="legal-documents" element={<AdminLegalDocuments />} />
              <Route path="footer-settings" element={<AdminFooterSettings />} />
              <Route path="payment-settings" element={<AdminPaymentSettings />} />
              <Route path="activity" element={<AdminLoginHistory />} />
              <Route path="clients/:clientId/login-history" element={<AdminLoginHistory />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
